import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FlipHorizontal, LogOut, Menu, X, Shield, Library, CreditCard, Home, Tag, Moon, Sun, Columns2, Music, Lock } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/hooks/use-theme";
import { Switch } from "@/components/ui/switch";
import { UpgradeModal } from "@/components/upgrade-modal";
import { isAudioFreeTrial } from "@/lib/constants";

export function AppHeader() {
  const auth = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [audioUpgradeOpen, setAudioUpgradeOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { isDark, toggleTheme } = useTheme();

  const goHome = (e: React.MouseEvent) => {
    e.preventDefault();
    if (window.location.pathname === "/" && window.location.search) {
      window.history.replaceState({}, "", "/");
      window.location.reload();
    } else {
      setLocation("/");
    }
  };

  const isLoggedIn = auth.isAuthenticated && auth.user;
  const isPaid = isLoggedIn && ["monthly", "yearly", "lifetime"].includes(auth.user!.subscriptionTier ?? "");
  const planLabel = isPaid
    ? auth.user!.subscriptionTier === "monthly" ? "Monthly Plan"
      : auth.user!.subscriptionTier === "lifetime" ? "Lifetime Plan"
      : "Yearly Plan"
    : null;

  return (
    <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
        <a href="/" onClick={goHome} className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-primary flex items-center justify-center">
            <FlipHorizontal className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
          </div>
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight">DanceMirror</h1>
        </a>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-2" data-testid="nav-desktop">
          <a href="/" onClick={goHome}>
            <Button variant="ghost" size="sm" data-testid="nav-home" className="text-sm">
              Home
            </Button>
          </a>
          {!isPaid && (
            <Link href="/pricing">
              <Button variant="ghost" size="sm" data-testid="nav-pricing" className="text-sm">
                Pricing
              </Button>
            </Link>
          )}
          {isLoggedIn ? (
            <>
              {isPaid && (
                <Link href="/compare">
                  <Button variant="ghost" size="sm" data-testid="nav-compare" className="text-sm">
                    <Columns2 className="w-3.5 h-3.5 mr-1" /> Compare
                    <span className="ml-1 text-[10px] font-medium px-1 py-0.5 rounded bg-muted text-muted-foreground leading-none">Beta</span>
                  </Button>
                </Link>
              )}
              {(isPaid || isAudioFreeTrial) ? (
                <Link href="/audio">
                  <Button variant="ghost" size="sm" data-testid="nav-audio" className="text-sm">
                    <Music className="w-3.5 h-3.5 mr-1" /> Audio
                  </Button>
                </Link>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="nav-audio"
                  className="text-sm"
                  onClick={() => setAudioUpgradeOpen(true)}
                >
                  <Music className="w-3.5 h-3.5 mr-1" /> Audio
                  <Lock className="w-2.5 h-2.5 ml-1 text-muted-foreground" />
                </Button>
              )}
              <Link href="/library">
                <Button variant="ghost" size="sm" data-testid="nav-library" className="text-sm">
                  My Library
                </Button>
              </Link>
              {auth.user!.isAdmin === "true" && (
                <Link href="/admin">
                  <Button variant="ghost" size="sm" data-testid="nav-admin" className="text-sm">
                    <Shield className="w-3.5 h-3.5 mr-1" /> Admin
                  </Button>
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" aria-label="Open user menu" data-testid="button-user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={auth.user!.profileImageUrl || undefined} alt={auth.user!.firstName || "User"} />
                      <AvatarFallback className="text-xs">
                        {(auth.user!.firstName?.[0] || "")}{(auth.user!.lastName?.[0] || "")}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium" data-testid="text-user-name">{auth.user!.firstName} {auth.user!.lastName}</p>
                    <p className="text-xs text-muted-foreground" data-testid="text-user-email">{auth.user!.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation("/library")} data-testid="menu-library">
                    <Library className="w-4 h-4 mr-2" />
                    My Library
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/pricing")} data-testid="menu-subscription">
                    <CreditCard className="w-4 h-4 mr-2" />
                    <span className="flex-1">Manage Subscription</span>
                    {planLabel && <span className="text-xs text-muted-foreground" data-testid="text-plan-label">{planLabel}</span>}
                  </DropdownMenuItem>
                  {auth.user!.isAdmin === "true" && (
                    <DropdownMenuItem onClick={() => setLocation("/admin")} data-testid="menu-admin">
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                      <span>{isDark ? "Dark mode" : "Light mode"}</span>
                    </div>
                    <Switch checked={isDark} onCheckedChange={toggleTheme} aria-label="Toggle dark mode" data-testid="toggle-theme" />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => { window.location.href = "/api/logout"; }}
                    data-testid="menu-logout"
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle dark mode" data-testid="toggle-theme-desktop-loggedout">
                {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </Button>
              <a href="/auth/google">
                <Button size="sm" data-testid="nav-login" className="text-sm">
                  Sign in with Google
                </Button>
              </a>
            </>
          )}
        </div>

        {/* Mobile hamburger only */}
        <Button
          variant="ghost"
          size="icon"
          className="sm:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="button-mobile-menu"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden border-t bg-card/95 backdrop-blur-sm" data-testid="nav-mobile-menu">
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
            {isLoggedIn && (
              <>
                <div className="flex items-center gap-3 px-3 py-2 mb-1">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={auth.user!.profileImageUrl || undefined} alt={auth.user!.firstName || "User"} />
                    <AvatarFallback className="text-xs">
                      {(auth.user!.firstName?.[0] || "")}{(auth.user!.lastName?.[0] || "")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium" data-testid="text-user-name-mobile">{auth.user!.firstName} {auth.user!.lastName}</p>
                    <p className="text-xs text-muted-foreground" data-testid="text-user-email-mobile">{auth.user!.email}</p>
                  </div>
                </div>
                <div className="border-t border-border mb-1" />
              </>
            )}

            <a href="/" onClick={(e) => { setMobileMenuOpen(false); goHome(e); }}>
              <Button variant="ghost" className="w-full justify-start" data-testid="nav-home-mobile">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </a>
            {!isPaid && (
              <Link href="/pricing" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="nav-pricing-mobile">
                  <Tag className="w-4 h-4 mr-2" />
                  Pricing
                </Button>
              </Link>
            )}

            {isLoggedIn ? (
              <>
                {isPaid && (
                  <Link href="/compare" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start" data-testid="nav-compare-mobile">
                      <Columns2 className="w-4 h-4 mr-2" />
                      Compare
                      <span className="ml-1 text-[10px] font-medium px-1 py-0.5 rounded bg-muted text-muted-foreground leading-none">Beta</span>
                    </Button>
                  </Link>
                )}
                {(isPaid || isAudioFreeTrial) ? (
                  <Link href="/audio" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start" data-testid="nav-audio-mobile">
                      <Music className="w-4 h-4 mr-2" />
                      Audio
                    </Button>
                  </Link>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    data-testid="nav-audio-mobile"
                    onClick={() => { setMobileMenuOpen(false); setAudioUpgradeOpen(true); }}
                  >
                    <Music className="w-4 h-4 mr-2" />
                    Audio
                    <Lock className="w-3 h-3 ml-auto text-muted-foreground" />
                  </Button>
                )}
                <Link href="/library" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start" data-testid="nav-library-mobile">
                    <Library className="w-4 h-4 mr-2" />
                    My Library
                  </Button>
                </Link>
                {auth.user!.isAdmin === "true" && (
                  <Link href="/admin" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start" data-testid="nav-admin-mobile">
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}
                <div className="border-t border-border my-1" />
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    <span>{isDark ? "Dark mode" : "Light mode"}</span>
                  </div>
                  <Switch checked={isDark} onCheckedChange={toggleTheme} aria-label="Toggle dark mode" data-testid="toggle-theme-mobile" />
                </div>
                <Link href="/pricing" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start" data-testid="menu-subscription-mobile">
                    <CreditCard className="w-4 h-4 mr-2" />
                    <span className="flex-1 text-left">Manage Subscription</span>
                    {planLabel && <span className="text-xs text-muted-foreground" data-testid="text-plan-label-mobile">{planLabel}</span>}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive hover:text-destructive"
                  onClick={() => { window.location.href = "/api/logout"; }}
                  data-testid="menu-logout-mobile"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <div className="border-t border-border my-1" />
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    <span>{isDark ? "Dark mode" : "Light mode"}</span>
                  </div>
                  <Switch checked={isDark} onCheckedChange={toggleTheme} aria-label="Toggle dark mode" data-testid="toggle-theme-mobile-loggedout" />
                </div>
                <a href="/auth/google">
                  <Button className="w-full" data-testid="nav-login-mobile">
                    Sign in with Google
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>
      )}

      <UpgradeModal
        open={audioUpgradeOpen}
        onOpenChange={setAudioUpgradeOpen}
        mode={auth.isAuthenticated ? "upgrade" : "login"}
        featureName="Audio Practice Player"
        returnTo="/audio"
      />
    </header>
  );
}
